import yup, {
  stringRequired,
  numberRequired,
  validUrl,
  addressValidation,
  dateRequired,
  minNumRequired,
  minNumNotRequired,
  isMinYear,
  isMaxCurrentYear,
  stringRequiredIfFieldHasXValue,
  allowedFileTypes,
  string,
  number,
  phoneNumber,
  currency,
} from "common/libs/yup";
import {
  parseDate,
  validateDateFormat,
  phoneNumberRegex,
  isAtleast18,
  isFileSizeGreaterThanMax,
} from "./validation.helpers";
import { listingGroupSectionReview } from "common/features/listings/validations/listingGroupSectionReview";
import { ConstructionType, ListingFoundationType } from "common/middleware/api.services";

// String
const stringRequiredIfContextIsFalse = (ctx: string) => string
  .when(`$${ctx}`, (ctx, schema) => {
    return !ctx ? schema.required() : schema;
  });
const stringRequiredIfIsCorporationAndIsFirstPurchaser = string
  .test("required", (val, context) => {
    if (
      context.options.context.isFirstPurchaser
      && context.options.context.isCorporation
    ) {
      return (
        val !== undefined
        && val !== null
        && val !== ""
      );
    }

    return true;
  });
const emailRequiredIfContextIsFalse = (ctx: string) => string
  .when(`$${ctx}`, (ctx, schema) => {
    return !ctx ? schema.email().required() : schema;
  });
const isStartTimeBeforeEndTimeRequired = string
  .test(
    "startTime",
    "invalid_startTime",
    (val, ctx) => ctx.parent.endTime
      ? ctx.parent.endTime > val
      : true
  )
  .required();
const isEndTimeAfterStartTimeRequired = string
  .test(
    "endTime",
    "invalid_endTime",
    (val, ctx) => ctx.parent.startTime
      ? ctx.parent.startTime < val
      : true
  )
  .required()

// Number
const minNum0MaxNum100NotRequired = number
  .transform((val) => isNaN(val) ? null : val)
  .nullable()
  .min(0, "minNum0")
  .max(100, "maxNum100");
const minNum0MustBeIntegerNotRequired = number
  .transform((val) => isNaN(val) ? null : val)
  .nullable()
  .min(0, "minNum0")
  .test(
    "integer",
    "integer",
    (val) => {
      if (val === undefined || val === null) {
        return true;
      }

      return Number.isInteger(val)
    }
  );
const numRequiredIfContextIsFalse = (ctx: string) => number
  .when(`$${ctx}`, (ctx, schema) => {
    return !ctx ? schema.required() : schema;
  });

// Date
const minDateTodayNotRequired = yup
  .date()
  .nullable()
  .min(new Date(new Date().setHours(0, 0, 0, 0)));

// Files
const maxFileSizeMaxNumOfFiles = (maxNumOfFiles: number, maxSizeInMb: number) => yup
  .mixed()
  .test(
    "maxNumOfFiles",
    `max${maxNumOfFiles}Files`,
    (files, ctx) => files.length + ctx.options.context.currNumOfFiles <= maxNumOfFiles
  )
  .test(
    "maxFileSize",
    `maxFileSize${maxSizeInMb}mb`,
    (files: FileList) => !Array.from(files).some(f => isFileSizeGreaterThanMax(f.size, maxSizeInMb))
  );

const firstName = string.required();
const lastName = string.required();
const password = string.required().min(8);
const confirmPassword = string.oneOf([yup.ref("password")]).required();
const selectionName = string.required();
const selectionDueDate = yup
  .date()
  .transform(parseDate)
  .test("format", validateDateFormat)
  .min(new Date())
  .required()
  .typeError("");
const email = string.email().required();
const birthDate = yup
  .date()
  .transform(parseDate)
  .test("format", validateDateFormat)
  .max(new Date())
  .test("minAge", isAtleast18)
  .required()
  .typeError("");
const countryOfResidencyOrCitizenship = string.required();
const cellPhoneNumber = string.required().matches(phoneNumberRegex);
const employmentIndustry = string.required();
const employerName = string.required();
const jobTitle = string.required();
const relationshipWithOtherPurchaser = string
  .when("$isFirstPurchaser", (isFirstPurchaser, schema) => {
    return isFirstPurchaser ? schema : schema.required();
  });
const reasonType = string
  .when("$isChangeRequest", (isChangeRequest, schema) => {
    return isChangeRequest ? schema.required() : schema;
  });
const reasonDescription = string
  .when("$isChangeRequest", (isChangeRequest, schema) => {
    return isChangeRequest ? schema.required() : schema;
  });
const optionName = string.required();
const optionDescription = string.required();
const commissionPercent = number
  .min(0)
  .max(100)
  .required();
const firmDateCommissionPercent = number
  .min(0)
  .max(100)
  .required();
const closingDateCommissionPercent = number
  .min(0)
  .max(100)
  .required();
const bonusAmount = number
  .when("$hasBonus", { is: true, then: (schema) => schema.min(0.01).required() });
const bonusReceivedOnPaymentSchedule = string
  .nullable()
  .when("$hasBonus", { is: true, then: (schema) => schema.required() });
const minimumNumberOfDealsRequiredForBonus = number
  .when("$hasBonus", { is: true, then: (schema) => schema.required() });
const noteScheduleDate = yup
  .date()
  .transform((value, original) => !original ? undefined : value)
  .when("noteType", {
    is: (type: string) => ["followUp", "appointment"].includes(type),
    then: schema => schema.required()
  });
const noteProject = string
  .when("noteType", {
    is: (type: string) => ["9", "13"].includes(type),
    then: schema => schema.required()
  });
const noteSubject = string
  .when("noteType", {
    is: (type: string) => [
      "followUp", "appointment", "inquiry", "9", "10", "13"
    ].includes(type),
    then: schema => schema.required()
  });
const noteLanguage = string
  .when("noteType", {
    is: (type: string) => ["9", "10", "13"].includes(type),
    then: schema => schema.required()
  });
const noteRecipient = string
  .when("noteType", {
    is: (type: string) => ["9", "10", "13"].includes(type),
    then: schema => schema.required()
  });
const emailCode = string.required();
const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  yesterday.setHours(23, 59, 0, 0);
const allocationStartDateRequired = yup
  .date()
  .min(yesterday)
  .required();
const allocationExpiryDateRequired = yup
  .date()
  .min(new Date())
  .required();
const allocationStartDate = yup
  .mixed()
  .nullable()
  .when("$startDateValidation", {
    is: true,
    then: yup.date().min(yesterday).required(),
});
const allocationExpiryDate = yup
  .mixed()
  .nullable()
  .when("$expiryDateValidation", {
    is: true,
    then: yup.date().min(new Date()).required(),
});
const openHouseDates = yup.array().of(yup.object({
  date: stringRequired,
  timeBlocks: yup.array().of(yup.object({
    startTime: isStartTimeBeforeEndTimeRequired,
    endTime: isEndTimeAfterStartTimeRequired,
  }))
}));

export const createAccountValidation = yup.object({
  firstName,
  lastName,
  email,
  password,
  confirmPassword,
});

export const registerBrokerValidation = yup.object({
  firstName,
  lastName,
  email,
  cellPhoneNumber,
  password,
  confirmPassword,
});

export const loginValidation = yup.object({ email, password });

export const forgotPassValidation = yup.object({ email });

export const resetPassValidation = yup.object({ password, confirmPassword });

export const purchaserValidation = yup.object({
  firstName,
  lastName,
  relationshipWithOtherPurchaser,
  birthDate,
  countryOfResidencyOrCitizenship,
  email,
  cellPhoneNumber,
  employmentIndustry,
  employerName,
  jobTitle,
  isFirstTimeHomeBuyer: stringRequiredIfContextIsFalse("isCorporation"),
  isPrincipalResidence: stringRequiredIfContextIsFalse("isCorporation"),
  hasPurchasedFromPrestiBefore: string.when(["$isCorporation", "$isPresti"], {
    is: (isCorporation: boolean, isPresti: boolean) => isCorporation === false && isPresti,
    then: schema => schema.required(),
  }),
  residentAddress: addressValidation,
  reasonType,
  reasonDescription,
  companyName: stringRequiredIfIsCorporationAndIsFirstPurchaser,
  companyAddress: yup.object({
    street: stringRequiredIfIsCorporationAndIsFirstPurchaser,
    city: stringRequiredIfIsCorporationAndIsFirstPurchaser,
    province: stringRequiredIfIsCorporationAndIsFirstPurchaser,
    postalCode: stringRequiredIfIsCorporationAndIsFirstPurchaser,
    country: stringRequiredIfIsCorporationAndIsFirstPurchaser,
  }),
});

export const requestReasonValidation = yup.object({
  //reasonType,
  reasonDescription,
});

export const optionValidation = yup.object({
  optionName,
  optionDescription,
});

export const newSelectionValidation = yup.object({
  selectionName,
  selectionDueDate,
});

export const profileValidation = yup.object({
  firstName,
  lastName,
  email,
  cellPhoneNumber,
  residentAddress: addressValidation,
  employmentIndustry,
  employerName,
  jobTitle,
  isFirstTimeHomeBuyer: stringRequired,
  isPrincipalResidence: stringRequired,
  hasPurchasedFromPrestiBefore: stringRequired,
});

export const addPurchaserValidation = yup.object({
  firstName,
  lastName,
  //relationshipWithOtherPurchaser,
  email,
  cellPhoneNumber,
});

export const buildingCommissionValidation = yup.object({
  commissionPercent,
  firmDateCommissionPercent,
  closingDateCommissionPercent,
  bonusAmount,
  bonusReceivedOnPaymentSchedule,
  minimumNumberOfDealsRequiredForBonus,
});

export const builderUserValidation = yup.object({
  email,
  roles: yup.array().min(1),
});

export const propertyValidation = yup.object({
  propertyName: stringRequiredIfContextIsFalse("isResale"),
  project: stringRequiredIfContextIsFalse("isResale"),
  propertyType: stringRequired,
  realEstateType: stringRequired,
  availability: stringRequired,
  basePrice: minNumRequired(0),
  address: addressValidation,
  lotType: stringRequired,
  completionDate: dateRequired,
});

export const noteValidation = yup.object({
  noteType: stringRequired,
  scheduleDate: noteScheduleDate,
  project: noteProject,
  subject: noteSubject,
  description: stringRequired,
  language: noteLanguage,
  recipient: noteRecipient,
});

export const allocatePropertiesRequiredValidation = yup.object({
  allocationStartDateRequired,
  allocationExpiryDateRequired
});

export const allocatePropertiesOptionalValidation = yup.object({
  allocationStartDate,
  allocationExpiryDate
});

export const verifyEmailValidation = yup.object({ emailCode });

const otpCode = string
  .required()
  .matches(/^\d{6}$/, "Must be a 6-digit code");

export const otpCodeValidation = yup.object({ otpCode });

export const emailValidation = yup.object({ email });

export const offerParamsValidation = yup.object({
  customizedPropertyPrice: number
    .test("minNum0", (price, context) => (
      price !== null ? price >= 0 : true
    )),
  customCreditCardHoldAmount: number
    .min(500)
    .max(5000),
  customDepositAmount: number
    .min(50000)
    .test("maxPrice", (price, context) => (
      price !== null
        ? price <= context.options.context.listPrice
        : true
    )),
  dueDiligenceDate: minDateTodayNotRequired,
  depositDueDate: minDateTodayNotRequired,
  financialDueDate: minDateTodayNotRequired,
  closingDate: minDateTodayNotRequired,
  isDepositReimbursable: yup.boolean(),
});

export const createBuilderValidation = yup.object({
  companyName: stringRequired,
});

export const sharePropertyValidation = yup.object({
  sender: yup.object({
    firstName: stringRequiredIfContextIsFalse("isLoggedIn"),
    lastName: stringRequiredIfContextIsFalse("isLoggedIn"),
    email: emailRequiredIfContextIsFalse("isLoggedIn"),
    role: numberRequired,
  }),
  recipients: yup.array().of(yup.object({
    firstName: stringRequiredIfContextIsFalse("isUsingExistingOpportunity"),
    lastName: stringRequiredIfContextIsFalse("isUsingExistingOpportunity"),
    email: emailRequiredIfContextIsFalse("isUsingExistingOpportunity"),
    role: numRequiredIfContextIsFalse("isUsingExistingOpportunity"),
  })),
});

export const connectionValidation = yup.object({
  firstName,
  lastName,
  cellPhoneNumber,
  email
});

export const listingPriceValidation = yup.object({
  newPrice: currency.required(),
});

export const openHouseValidation = yup.object({
  openHouseDates: openHouseDates
});

export const ownershipProofValidation = yup.object({
  files: yup.array().of(yup.object({
    documentType: string.when(["$hasUploadedDoc", "file"], {
      is: (hasUploadedDoc: boolean, file: FileList) => !hasUploadedDoc || Boolean(file),
      then: schema => schema.required(),
    }),
    year: number.when(["$hasUploadedDoc", "file", "documentType"], {
      is: (hasUploadedDoc: boolean, file: FileList, documentType: string) => {
        return documentType === "propertyTaxBill" && (!hasUploadedDoc || Boolean(file))
      },
      then: schema => schema.required(),
    }),
    otherDocumentType: string.when(["$hasUploadedDoc", "file", "documentType"], {
      is: (hasUploadedDoc: boolean, file: FileList, documentType: string) => {
        return documentType === "other" && (!hasUploadedDoc || Boolean(file))
      },
      then: schema => schema.required(),
    }),
    file: allowedFileTypes.when("$hasUploadedDoc", {
      is: false,
      then: schema => schema.required(),
    }),
  })),
  comment: string.max(200, "exceededCharacterCount"),
});

export const contractTypeValidation = yup.object({
  contractType: stringRequired,
});

export const imageCategoryValidation = yup.object({
  name: stringRequired,
});

export const uploadCategoryMediaValidation = yup.object({
  files: yup.mixed().when("$mediaType", {
    is: "video",
    then: maxFileSizeMaxNumOfFiles(20, 100),
    otherwise: maxFileSizeMaxNumOfFiles(20, 6)
  }),
});

export const addImageCategoryValidation = yup.object({
  name: stringRequired,
});

export const propertyInfoValidation = yup.object({
  propertyType: stringRequired,
  foundationType: stringRequired,
  foundationTypeOther: stringRequiredIfFieldHasXValue("foundationType", [ListingFoundationType.Other.toString()]),
  constructionType: stringRequired,
  constructionTypeOther: stringRequiredIfFieldHasXValue("constructionType", [ConstructionType.Other.toString()]),
  yearBuilt: isMinYear(1800).concat(isMaxCurrentYear).required(),
  hasCoOwnership: stringRequired,
  coOwnershipType: string.when("hasCoOwnership", {
    is: "yes",
    then: schema => schema.required(),
  }),
  ownershipPercentage: minNum0MaxNum100NotRequired,
  address: addressValidation,
  landRegistryType: stringRequired,
  landRegistry: stringRequired,
  numberOfBedrooms: minNum0MustBeIntegerNotRequired.required(),
  numberOfBathrooms: minNum0MustBeIntegerNotRequired,
  numberOfPowderRooms: minNum0MustBeIntegerNotRequired,
  isMultiLevel: stringRequired,
  levels: yup.array().of(yup.object({
    lengthFeet: minNumNotRequired(0),
    lengthInches: minNumNotRequired(0),
    widthFeet: minNumNotRequired(0),
    widthInches: minNumNotRequired(0),
    heightFeet: minNumNotRequired(0),
    heightInches: minNumNotRequired(0),
    lengthInMeters: minNumNotRequired(0),
    widthInMeters: minNumNotRequired(0),
    heightInMeters: minNumNotRequired(0),
    totalListingAreaInSqMeters: minNumNotRequired(0),
    totalPropertyAreaInSqFt: minNumNotRequired(0),
  })),
  totalIndoorAreaInSqMeters: minNumNotRequired(0),
  totalIndoorAreaInSqFt: minNumNotRequired(0),
  outdoorLengthFeet: minNumNotRequired(0),
  outdoorLengthInches: minNumNotRequired(0),
  outdoorLengthInMeters: minNumNotRequired(0),
  outdoorWidthFeet: minNumNotRequired(0),
  outdoorWidthInches: minNumNotRequired(0),
  outdoorWidthInMeters: minNumNotRequired(0),
  totalOutdoorAreaInSqMeters: minNumNotRequired(0),
  totalOutdoorAreaInSqFt: minNumNotRequired(0),
  totalIndoorOutdoorAreaInSqMeters: minNumRequired(0),
  totalIndoorOutdoorAreaInSqFt: minNumRequired(0),
  lotLengthInMeters: minNumNotRequired(0),
  lotWidthInMeters: minNumNotRequired(0),
  lotLengthFeet: minNumNotRequired(0),
  lotLengthInches: minNumNotRequired(0),
  lotWidthFeet: minNumNotRequired(0),
  lotWidthInches: minNumNotRequired(0),
  supplementaryDocs: allowedFileTypes,
  propertyStatus: stringRequired,
  hasFeatures: stringRequired,
});

export const propertyFeaturesValidation = yup.object({
  hasParking: string.required(),
  hasStorage: string.required(),
  storageUnits: yup.array().of(yup.object({
    lengthFeet: minNumNotRequired(0),
    lengthInches: minNumNotRequired(0),
    widthFeet: minNumNotRequired(0),
    widthInches: minNumNotRequired(0),
    heightFeet: minNumNotRequired(0),
    heightInches: minNumNotRequired(0),
    lengthInMeters: minNumNotRequired(0),
    widthInMeters: minNumNotRequired(0),
    heightInMeters: minNumNotRequired(0)
  })),
  scheduleAViewLink: validUrl,
  contactPhoneNumber: phoneNumber,
  openHouseDates: openHouseDates
})

export const reviewSellerValidation = yup.object({
  nameReview: listingGroupSectionReview,
  aboutReview: listingGroupSectionReview,
  contactReview: listingGroupSectionReview,
})

export const reviewSellerTermsConditionsValidation = yup.object({
  spousalReview: listingGroupSectionReview,
  preEmptiveReview: listingGroupSectionReview,
})

export const reviewPropertyInfoValidation = yup.object({
  propertyTypeReview: listingGroupSectionReview,
  coOwnershipReview: listingGroupSectionReview,
  addressReview: listingGroupSectionReview,
  overviewReview: listingGroupSectionReview,
  propertyDimensionsReview: listingGroupSectionReview,
  lotDimensionsReview: listingGroupSectionReview,
  supplementaryDocsReview: listingGroupSectionReview,
  featuresReview: listingGroupSectionReview,
})

export const reviewPropertyFeaturesValidation = yup.object({
  parkingReview: listingGroupSectionReview,
  storageReview: listingGroupSectionReview,
  viewReview: listingGroupSectionReview,
  openHouseReview: listingGroupSectionReview,
  viewingScheduleReview: listingGroupSectionReview,
  contactReview: listingGroupSectionReview
})

export const reviewPropertyTermsValidation = yup.object({
  paymentStructureReview: listingGroupSectionReview,
  paymentTermsReview: listingGroupSectionReview,
  otherFeesReview: listingGroupSectionReview,
  legalRepReview: listingGroupSectionReview,
})

export const ownershipProofReviewValidation = yup.object({
  ownershipProofReview: listingGroupSectionReview,
})

export const reviewImagesValidation = yup.object({
  imagesReview: listingGroupSectionReview
})

export const contractReviewValidation = yup.object({
  contractReview: listingGroupSectionReview,
})
